import type { UnknownAction } from "@reduxjs/toolkit";
import { combineReducers, configureStore, createAction } from "@reduxjs/toolkit";
import type { PersistConfig } from "redux-persist";
import { FLUSH, PAUSE, PERSIST, persistReducer, PURGE, REGISTER, REHYDRATE } from "redux-persist";
import type { PersistPartial } from "redux-persist/es/persistReducer";

import authReducer from "./slices/authSlice";
import baggageReducer from "./slices/baggageSlice";
import bookingReducer from "./slices/bookingSlice";
import bundleReducer from "./slices/bundleSlice";
import checkinReducer from "./slices/checkinSlice";
import cmsReducer from "./slices/cmsSlice";
import corporateReducer from "./slices/corporateSlice";
import extrasReducer from "./slices/extrasSlice";
import flightsAvailabilityReducer from "./slices/flightsAvailabilitySlice";
import flightReducer from "./slices/flightSlice";
import kontentReducer from "./slices/kontentSlice";
import localeReducer from "./slices/localeSlice";
import manageBookingReducer from "./slices/manageBookingSlice";
import modifyBookingReducer from "./slices/modifyBookingSlice";
import passengerReducer from "./slices/passengerSlice";
import paymentReducer from "./slices/paymentSlice";
import promotionReducer from "./slices/promotionSlice";
import seatMapReducer from "./slices/seatMapSlice";
import stationsReducer from "./slices/stationsSlice";
import walletReducer from "./slices/walletSlice";
import storage from "./storage";

// Reset action
export const resetApp = createAction("app/reset");
export const resetAppForFlightZone = createAction("app/resetFlightZone");
// New: hard reset for landing page (clear everything except auth and locale)
export const resetToMain = createAction("app/resetToMain");
/**
 * Reset for LOGOUT. Distinct from every other reset here because it is the only one that
 * must drop `auth` and the previous user's data.
 *
 * `resetApp` and `resetToMain` both deliberately keep `state.auth` (and `passenger`),
 * which is correct for in-session navigation and wrong on the logout landing page: the
 * old per-tab blob has already been rehydrated into memory by then, so preserving those
 * fields re-persists the previous user under the freshly rotated session key. Measured
 * live: logging out from a zone left main's header on the previous user, with a store
 * byte-identical to the pre-logout one except for the ~137-byte auth payload.
 */
export const resetForLogout = createAction("app/resetForLogout");
// New: reset when search params (destination/date/passengers) change
export const resetForSearchChange = createAction("app/resetForSearchChange");
// New: reset when bundle is edited (keep destination and selected journeys)
export const resetForBundleEdit = createAction("app/resetForBundleEdit");

// Combined reducer
const appReducer = combineReducers({
  corporate: corporateReducer,
  flight: flightReducer,
  flightsAvailability: flightsAvailabilityReducer,
  seatMap: seatMapReducer,
  extras: extrasReducer,
  baggage: baggageReducer,
  bundle: bundleReducer,
  passenger: passengerReducer,
  payment: paymentReducer,
  booking: bookingReducer,
  checkinFlow: checkinReducer,
  kontent: kontentReducer,
  manageBooking: manageBookingReducer,
  modifyBooking: modifyBookingReducer,
  locale: localeReducer,
  promotion: promotionReducer,
  auth: authReducer,
  cms: cmsReducer,
  stations: stationsReducer,
  wallet: walletReducer,
});

export type AppState = ReturnType<typeof appReducer>;

export const PERSIST_KEY = "redux";

/**
 * Transform to add timestamp to persisted Redux state
 * This allows us to detect and cleanup orphaned data
 */
const timestampTransform = {
  in: (state: AppState) => {
    // Add timestamp when saving to localStorage
    return {
      ...state,
      _timestamp: Date.now(),
    };
  },
  out: (state: AppState & { _timestamp?: number }) => {
    // Remove timestamp when rehydrating (don't need it in Redux state)
    const { _timestamp, ...cleanState } = state;
    return cleanState as AppState;
  },
};

// Persist config typed
const persistConfig: PersistConfig<AppState> = {
  key: PERSIST_KEY,
  storage,
  whitelist: [
    "corporate",
    "flight",
    "extras",
    "baggage",
    "passenger",
    "payment",
    "seatMap",
    "booking",
    "checkinFlow",
    "promotion",
    "auth",
    "cms",
    "manageBooking",
    "modifyBooking",
    "stations",
  ],
  transforms: [timestampTransform],
  // Increase timeout to prevent "rehydrate called after timeout" warnings
  // This gives enough time for SessionIdHydrator to initialize the session ID
  timeout: 15000, // 15 seconds (default is ~25s, but we want explicit control)
};

// Root reducer with logic for reset and rehydrate
const rootReducer = (state: AppState | undefined, action: UnknownAction): AppState => {
  if (action.type === REHYDRATE) {
    const incoming = (action.payload ?? {}) as Partial<AppState>;

    const newState = {
      ...state,
      corporate: incoming.corporate ?? state?.corporate,
      promotion: incoming.promotion ?? state?.promotion,
      passenger: incoming.passenger ?? state?.passenger,
      auth: incoming.auth ?? state?.auth,
      payment: incoming.payment ?? state?.payment,
      booking: incoming.booking ?? state?.booking,
      // seatMap: incoming.seatMap ?? state?.seatMap,
      manageBooking: incoming.manageBooking ?? state?.manageBooking,
      checkinFlow: incoming.checkinFlow ?? state?.checkinFlow,
    } as AppState;
    return appReducer(newState, action);
  }

  // Full landing reset: clear everything except auth, locale and passengers
  if (action.type === resetToMain.type) {
    const incoming = (action.payload ?? {}) as Partial<AppState>;

    const newState = {
      auth: state?.auth,
      // NOTE: We deliberately keep passenger data across this reset so that
      // refreshing or returning to the landing page does not wipe passenger info.
      // If a flow really needs to clear passengers, it should dispatch
      // `clearPassengers` from `passengerSlice` explicitly.
      passenger: state?.passenger,
      locale: {
        locale: state?.locale.locale,
        currency: state?.locale.currency,
      },
      modifyBooking: incoming.modifyBooking ?? state?.modifyBooking,
      stations: state?.stations,
    } as AppState;
    return appReducer(newState, action);
  }

  if (action.type === resetForLogout.type) {
    // Keep only what is not the user's: the market they are browsing and the station
    // reference data. Everything else - auth, passenger, booking, payment - belongs to
    // the session being ended and must not survive it, on a shared machine especially.
    const newState = {
      locale: {
        locale: state?.locale.locale,
        currency: state?.locale.currency,
      },
      stations: state?.stations,
    } as AppState;
    return appReducer(newState, action);
  }

  if (action.type === resetApp.type) {
    const newState = {
      corporate: {
        ...state?.corporate,
        inboundSubtotal: 0, //reset total count on reset
        outboundSubtotal: 0, //reset total count on reset
      },
      promotion: state?.promotion,
      passenger: state?.passenger,
      auth: state?.auth,
      locale: {
        locale: state?.locale.locale,
        currency: state?.locale.currency,
      },
      stations: state?.stations,
    } as AppState;
    return appReducer(newState, action);
  }

  if (action.type === resetAppForFlightZone.type) {
    const newState = {
      corporate: state?.corporate,
      flight: state?.flight,
      auth: state?.auth,
      booking: state?.booking,
      promotion: state?.promotion,
      passenger: state?.passenger,
      locale: {
        locale: state?.locale.locale,
        currency: state?.locale.currency,
      },
      stations: state?.stations,
    } as AppState;
    return appReducer(newState, action);
  }

  // On corporate search/date/passenger change: clear selected bundles and all booking flows
  if (
    action.type === "corporate/setCorporateSearch" ||
    action.type === "corporate/setCorporateTravelDates" ||
    action.type === resetForSearchChange.type
  ) {
    const newState = {
      corporate: state?.corporate,
      auth: state?.auth,
      booking: state?.booking,
      promotion: state?.promotion,
      locale: {
        locale: state?.locale.locale,
        currency: state?.locale.currency,
      },
      flight: state?.flight,
      cms: state?.cms, // Keep CMS content (labels, common info)
      manageBooking: state?.manageBooking,
      modifyBooking: state?.modifyBooking,
      stations: state?.stations,
    } as AppState;
    return appReducer(newState, action);
  }

  // When user edits selected bundle: keep destination/search and current flight selection, clear downstream extras/baggage/passenger/payment/seat map/booking etc.
  if (action.type === "flight/setBundle" || action.type === resetForBundleEdit.type) {
    const newState = {
      corporate: state?.corporate,
      flight: state?.flight,
      flightsAvailability: state?.flightsAvailability,
      promotion: state?.promotion,
      auth: state?.auth,
      locale: {
        locale: state?.locale.locale,
        currency: state?.locale.currency,
      },
      cms: state?.cms,
      stations: state?.stations,
    } as AppState;
    return appReducer(newState, action);
  }

  if (action.type === "TEST") {
    const incoming = (action.payload ?? {}) as Partial<AppState>;
    const newState = {
      ...state,
      ...incoming,
    } as AppState;
    return appReducer(newState, action);
  }

  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export type PersistedAppState = AppState & PersistPartial;
export const makeStore = (preloadedState?: Partial<PersistedAppState>) =>
  configureStore({
    reducer: persistedReducer,
    preloadedState: preloadedState as unknown as PersistedAppState,
    devTools: process.env.NODE_ENV !== "production",
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  });

export const store = makeStore();

// Types
export type AppStore = ReturnType<typeof makeStore>;
export type RootState = ReturnType<AppStore["getState"]>;
export type AppDispatch = AppStore["dispatch"];
